import React, { useState } from "react";
import * as yup from "yup";
import useFormSteps from "./useFormSteps";
import { Formik } from "formik";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import useFormSubmission from "./useFormSubmit";
import { DialogTitle, IconButton } from "@mui/material";
import { X, ArrowRight, Check } from "@phosphor-icons/react";
import LoadingButton from "@mui/lab/LoadingButton";
import { useGlobalToast } from "components/GlobalToastProvider";

const MultiStepFormDialog = ({
  title,
  initialStep = 0,
  steps,
  defaultValues,
  onSubmit,
  open,
  onClose,
  classes = {},
  submitLabel = "Submit",
  successMessage,
  confirmAction,
  onConfirmComplete,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [confirmCompleted, setConfirmCompleted] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const { showToast } = useGlobalToast();
  const { loading, success, handleFormSubmit } = useFormSubmission(async (values) => {
    setFormValues(values);
    const result = await onSubmit(values);
    setSubmitResult(result);
    return result;
  });
  const { currentStep, goToNextStep, goToPrevStep, isFirstStep, isLastStep } = useFormSteps(
    initialStep,
    steps.length - 1,
    null
  );

  const FormComponent = steps[currentStep];

  const validationSchema = yup.object().shape({
    provider: yup.object().when([], {
      is: () => defaultValues?.provider !== undefined,
      then: (schema) => schema.shape({
        type: yup.string().required("Provider Type required"),
        id: yup.string().required("Provider required"),
        firstName: yup.string().when("id", {
          is: "new",
          then: (s) => s.required("First name is required"),
        }),
        lastName: yup.string().when("id", {
          is: "new",
          then: (s) => s.required("Last name is required"),
        }),
        email: yup.string().when("id", {
          is: "new",
          then: (s) => s.email("Enter a valid email address").required("Email address is required"),
        }),
        phone: yup.string().when("id", {
          is: "new",
          then: (s) => s.required("Contact information is required"),
        }),
        physicalAddress: yup.string().when(["id", "privatePractice"], {
          is: (id, privatePractice) => id === "new" && privatePractice,
          then: (s) => s.required("Address is required"),
        }),
        city: yup.string().when(["id", "privatePractice"], {
          is: (id, privatePractice) => id === "new" && privatePractice,
          then: (s) => s.required("City is required"),
        }),
        state: yup.string().when(["id", "privatePractice"], {
          is: (id, privatePractice) => id === "new" && privatePractice,
          then: (s) => s.required("State is required"),
        }),
        zipCode: yup.string().when(["id", "privatePractice"], {
          is: (id, privatePractice) => id === "new" && privatePractice,
          then: (s) => s.required("Zip code is required"),
        }),
        physicianClinic: yup.array().when(["id", "privatePractice"], {
          is: (id, privatePractice) => id === "new" && !privatePractice,
          then: (s) => s.min(1, "Select at least one clinic, or set the provider as a private practice"),
        }),
      }),
    }),
    document: yup.object().when([], {
      is: () => defaultValues?.document !== undefined,
      then: (schema) => schema.shape({
        title: yup.string().required("Please enter the title of the request"),
      }),
    }),
    otherDescription: yup.string().when("other", {
      is: true,
      then: (schema) => schema.required("Please enter a description of the requested information"),
    }),
    checkboxes: yup.mixed().nullable().test("checkboxes", "At least one item must be selected.", function (_, context) {
      const values = context.parent;
      if (isLastStep && !values.isEdit) {
        const checkboxFields = [
          "historyAndPhysicalExamination",
          "diagnosticReportOrTesting",
          "progressNotes",
          "psychologicalReports",
          "educationPlan",
          "treatmentPlanAndGoals",
          "other",
        ];
        const isChecked = checkboxFields.some((field) => !!values[field]);
        if (!isChecked) {
          return false;
        }
      }
      return true;
    }),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          margin: 0,
          boxSizing: "border-box",
          width: "444px",
        },
      }}
    >
      {success && successMessage ? (
        <>
          <DialogTitle>
            {title}
            <IconButton aria-label="close" onClick={() => { onConfirmComplete?.(); onClose(); }}>
              <X />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
              <Check size={24} color="green" weight="bold" />
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="subtitle1" align="center">
                  {successMessage.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {successMessage.description}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions>
            {confirmAction && !confirmCompleted ? (
              <LoadingButton
                loading={confirmLoading}
                onClick={async () => {
                  setConfirmLoading(true);
                  try {
                    await confirmAction.handler(submitResult?.physicianId, formValues);
                    setConfirmCompleted(true);
                  } catch {
                    showToast({ message: "Whoops! Something went wrong", errorState: true });
                  } finally {
                    setConfirmLoading(false);
                  }
                }}
              >
                {confirmAction.label}
              </LoadingButton>
            ) : (
              <Button color="secondary" onClick={() => { onConfirmComplete?.(); onClose(); }}>
                Close
              </Button>
            )}
          </DialogActions>
        </>
      ) : (
      <Formik
        initialValues={defaultValues || {}}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        validateOnBlur={true}
        validateOnChange={false}
      >
        {(formik) => {
          const validateAndProceed = async () => {
            const errors = await formik.validateForm();
            const hasProviderErrors = errors.provider?.type || errors.provider?.id ||
              errors.provider?.firstName || errors.provider?.lastName ||
              errors.provider?.email || errors.provider?.phone ||
              errors.provider?.physicalAddress || errors.provider?.city ||
              errors.provider?.state || errors.provider?.zipCode ||
              errors.provider?.physicianClinic;

            if (currentStep === 0 && hasProviderErrors) {
              formik.setTouched({
                provider: {
                  type: true,
                  id: true,
                  firstName: true,
                  lastName: true,
                  email: true,
                  phone: true,
                  physicalAddress: true,
                  city: true,
                  state: true,
                  zipCode: true,
                },
              });
              setSubmitAttempted(true);
            } else {
              setSubmitAttempted(false);
              goToNextStep();
            }
          };

          const handleFormSubmitWithValidation = (e) => {
            e.preventDefault();
            setSubmitAttempted(true);
            formik.handleSubmit(e);
          };

          return (
            <form onSubmit={handleFormSubmitWithValidation}>
              <DialogTitle>
                {title}
                <IconButton aria-label="close" onClick={onClose}>
                  <X />
                </IconButton>
              </DialogTitle>
              <Divider />
              <DialogContent className={classes.dialogContent}>
                <FormComponent submitAttempted={submitAttempted} />
              </DialogContent>
              <Divider />
              <DialogActions>
                {!isFirstStep && (
                  <Button onClick={goToPrevStep} disabled={loading} variant="text" color="primary">
                    Back
                  </Button>
                )}
                <Button color="secondary" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                {!isLastStep && (
                  <Button onClick={validateAndProceed} disabled={loading} endIcon={<ArrowRight />}>
                    Next
                  </Button>
                )}
                {isLastStep && (
                  <LoadingButton type="submit" loading={loading} endIcon={<ArrowRight />}>
                    {submitLabel}
                  </LoadingButton>
                )}
              </DialogActions>
            </form>
          );
        }}
      </Formik>
      )}
    </Dialog>
  );
};

export default MultiStepFormDialog;
